import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle, ArrowRight, Package } from 'lucide-react';

const GCR_MERCHANT_ID = 5813654237;

// Google Customer Reviews opt-in survey. Fired on the order confirmation page
// with the real order details so Google can email the buyer a review request.
function renderGoogleCustomerReviews({ orderId, email, country }) {
  if (!orderId || !email) return;

  // Estimated delivery date = today + 14 days (ships within 3 business days + transit).
  const eta = new Date();
  eta.setDate(eta.getDate() + 14);
  const estimatedDeliveryDate = eta.toISOString().slice(0, 10); // YYYY-MM-DD

  const config = {
    merchant_id: GCR_MERCHANT_ID,
    order_id: String(orderId),
    email: email,
    delivery_country: country || 'US',
    estimated_delivery_date: estimatedDeliveryDate,
  };

  window.renderOptIn = function () {
    window.gapi.load('surveyoptin', function () {
      window.gapi.surveyoptin.render(config);
    });
  };

  // If the Google platform library is already loaded (SPA re-navigation), render now.
  if (window.gapi && window.gapi.surveyoptin) {
    window.gapi.surveyoptin.render(config);
    return;
  }
  if (window.gapi && window.gapi.load) {
    window.renderOptIn();
    return;
  }

  // Otherwise inject the platform.js script which calls renderOptIn on load.
  if (!document.getElementById('google-platform-js')) {
    const script = document.createElement('script');
    script.id = 'google-platform-js';
    script.src = 'https://apis.google.com/js/platform.js?onload=renderOptIn';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }
}

const OrderSuccess = () => {
  const location = useLocation();
  const { orderId, email, country } = location.state || {};

  useEffect(() => {
    renderGoogleCustomerReviews({ orderId, email, country });
  }, [orderId, email, country]);

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-20" data-testid="order-success-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl p-12 shadow-xl text-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={64} className="text-green-600" />
          </div>

          <h1 className="text-4xl font-bold text-gray-900 mb-4">Order Placed Successfully!</h1>
          <p className="text-xl text-gray-600 mb-8">
            Thank you for your purchase. Your order has been received and is being processed.
          </p>

          {orderId && (
            <p className="text-sm text-gray-500 mb-8" data-testid="order-success-id">
              Order reference: <span className="font-semibold text-gray-700">{orderId}</span>
            </p>
          )}

          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Package size={24} className="text-cyan-600" />
              <h2 className="text-xl font-semibold text-gray-900">What's Next?</h2>
            </div>
            <ul className="space-y-3 text-left max-w-md mx-auto">
              <li className="flex items-start space-x-3">
                <CheckCircle size={20} className="text-cyan-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">You'll receive an order confirmation email shortly</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle size={20} className="text-cyan-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">We'll notify you when your order ships</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle size={20} className="text-cyan-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Track your order status in your email</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/products"
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all space-x-2"
            >
              <span>Continue Shopping</span>
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-cyan-500 hover:text-cyan-600 transition-all"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
